import React, { memo, useEffect, useState } from 'react';
import List from '@mui/material/List';

import sx from './Menu.styles';

import { useLocation } from 'react-router-dom';
import ExpandLessIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded';
import Collapse from '@mui/material/Collapse';

import MenuItem from './MenuItem';

// returns all prefix paths for the specified path: e.g. given '/a/b/c', the method returns ['/a', '/a/b', '/a/b/c']
const getPrefixPaths = path => {
  const segments = path.split('/').filter(segment => segment !== '');
  return segments.reduce((prefixPaths, segment) => {
    const currentPath = prefixPaths.length > 0 ? prefixPaths[prefixPaths.length - 1] + '/' + segment : '/' + segment;
    prefixPaths.push(currentPath);
    return prefixPaths;
  }, []);
};

const Menu = ({ items, sx: sxFromProps = {} }) => {
  const location = useLocation();

  // stores flags, indexed by path, specifying if the menu item for the given path (with subitems) is open or closed
  const [open, setOpen] = useState({});

  // updates the open flags when the location changes (even if the path itself doesn't change)
  useEffect(() => {
    // set the open flag to true for all prefix paths of the current path
    const newOpen = getPrefixPaths(location.pathname).reduce((newOpen, path) => ({ ...newOpen, [path]: true }), {});

    // Close all dropdowns that are not part of the current path
    const currentPrefixPaths = getPrefixPaths(location.pathname);
    const updatedOpen = { ...open };

    // Close any dropdown that's not in the current path
    Object.keys(updatedOpen).forEach(path => {
      if (!currentPrefixPaths.includes(path)) {
        updatedOpen[path] = false;
      }
    });

    setOpen({ ...updatedOpen, ...newOpen });
  }, [location]);

  // checks whether the specified menu item is open or closed
  const isOpen = item => !!open[item.href];

  return (
    <List sx={{ ...sx.list, ...sxFromProps }} data-test="menu">
      {items.map((item, index) => {
        const hasItems = !!item.items?.length;
        const onClick = event => {
          event.preventDefault();
          setOpen(open => ({ ...open, [item.href]: !open[item.href] }));
        };
        const expand = isOpen(item) ? <ExpandLessIcon onClick={onClick} /> : <ExpandMoreIcon onClick={onClick} />;
        return (
          <div key={index}>
            <MenuItem item={item} expand={hasItems && expand} isChildItem={false} setOpen={setOpen} />
            {hasItems && (
              <Collapse in={isOpen(item)}>
                <List component="div" disablePadding>
                  {item.items.map((item, index) => (
                    <div key={index}>
                      <MenuItem item={item} expand={null} isChildItem={true} setOpen={setOpen} />
                    </div>
                  ))}
                </List>
              </Collapse>
            )}
          </div>
        );
      })}
    </List>
  );
};

export default memo(Menu);
